/**
 * @mhmo91/ican — permission helpers.
 *
 * Permission strings have shape `<namespace>.<verb>` (e.g.
 * `owl.orders.dispatchOrder`). Three wildcard levels, expanded at
 * check time:
 *
 *   - `*`                  — global wildcard (typically the built-in
 *                            `owner` role only)
 *   - `<namespace>.*`      — all verbs in a namespace
 *   - `<perm>` (exact)     — exact match
 *
 * The check is its own `hasPermission` AST node: the evaluator expands
 * the wildcards against the actor's permissions, the Firestore emitter
 * against the role document the caller's grant names.
 */
import { fieldPathOf } from './proxy.ts';
import type { Cond, FieldRef, HasPermissionOptions } from './types.ts';

/**
 * Expand a permission string to the list of permission tokens that
 * grant it. A user's permissions array satisfies the check if it
 * contains any one of these tokens.
 *
 * @example
 *   expandPermission('owl.orders.dispatchOrder')
 *   // → ['owl.orders.dispatchOrder', 'owl.orders.*', '*']
 */
export const expandPermission = (perm: string): readonly string[] => {
  const lastDot = perm.lastIndexOf('.');
  if (lastDot === -1) return Object.freeze([perm, '*']);
  const namespace = perm.slice(0, lastDot);
  return Object.freeze([perm, `${namespace}.*`, '*']);
};

/**
 * Build a `Cond` that holds when the actor's permissions list grants
 * the given permission. Produces a dedicated `hasPermission` AST node
 * so the runtime evaluator and the Firestore emitter can each render
 * it in their own way:
 *
 *   - **runtime** — read `me.permissions` (or whatever path was
 *     passed) and assert it contains an exact / namespace-wildcard /
 *     global-wildcard match.
 *   - **Firestore** — emit a call to `hasPermission(uid, wsId, [...])` that
 *     reads the grant doc at `users/{uid}/grants/{wsId}` and the role from
 *     `workspaces/{wsId}/roles/{roleId}`. One grant document answers it: a
 *     grant is only ever filed at an organization id, and the organization is
 *     the path variable on every tenant-owned collection.
 *
 * Use inside a `.when(...)` callback. The first argument is a
 * FieldRef pointing at the actor's permissions array (typically
 * `me.permissions`); the second is the static permission string the
 * rule guards.
 *
 * `{ workspace }` names the workspace the permission is held in as a literal
 * id, for a document whose own path names some other tenant:
 *
 *   hasPermission(me.permissions, 'catalog.curate', { workspace: 'hq' })
 *
 * The emitted call takes the id quoted — `hasPermission(uid, "hq", [...])` —
 * so it resolves on any path and in any list. The runtime evaluator answers it
 * only for an actor whose active workspace is that id.
 */
export const hasPermission = (
  permsRef: FieldRef<readonly string[]>,
  permission: string,
  { workspace, workspaceField, scope, event }: HasPermissionOptions = {},
): Cond => {
  const path = fieldPathOf(permsRef);
  if (path === undefined) {
    throw new Error(
      '@mhmo91/ican: hasPermission(...) expected a FieldRef (e.g. me.permissions) as the first argument',
    );
  }
  // One leaf, one workspace source. Accepting both would leave which one wins
  // to the emitter and the evaluator to agree on separately, which is the
  // disagreement this kernel exists to make impossible.
  if (workspace !== undefined && workspaceField !== undefined) {
    throw new Error(
      '@mhmo91/ican: hasPermission(..., { workspace, workspaceField }) names two workspace sources — pass one',
    );
  }
  const workspacePath = workspaceField === undefined || typeof workspaceField === 'string'
    ? undefined : fieldPathOf(workspaceField);
  if (workspaceField !== undefined && (typeof workspaceField === 'string'
    ? workspaceField.length === 0 : workspacePath === undefined)) {
    throw new Error(
      '@mhmo91/ican: hasPermission(..., { workspaceField }) expected a field name on the document',
    );
  }
  // The id is interpolated into a document path: '' and '/' address a different
  // document than the grant and role the rule means, and Firestore refuses '.',
  // '..' and '__…__' as document ids.
  if (
    workspace !== undefined &&
    (['', '.', '..'].includes(workspace) || workspace.includes('/') || /^__.*__$/.test(workspace))
  ) {
    throw new Error(
      `@mhmo91/ican: hasPermission(..., { workspace }) expected a workspace id usable as a Firestore document id — non-empty, no '/', not '.' or '..', not '__…__' — got ${JSON.stringify(workspace)}`,
    );
  }
  const scopePath = scope === undefined ? undefined : fieldPathOf(scope.id ?? scope.ids ?? scope.keys);
  if (
    scope !== undefined &&
    (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(scope.roleField) || scopePath === undefined)
  ) {
    throw new Error('@mhmo91/ican: permission scope requires a role field and a FieldRef');
  }
  if (event !== undefined && scope !== undefined) throw new Error('@mhmo91/ican: event grant and role subject scope cannot be combined');
  const eventField = event === undefined ? undefined : fieldPathOf(event);
  if (event !== undefined && eventField === undefined) throw new Error('@mhmo91/ican: event requires a FieldRef');
  return Object.freeze({
    kind: 'hasPermission' as const,
    ...(eventField === undefined ? {} : { eventField }),
    permsField: path,
    permission,
    ...(workspace === undefined ? {} : { workspace }),
    ...(typeof workspaceField === 'string' ? { workspaceField } : workspacePath === undefined ? {} : { workspaceField: workspacePath }),
    ...(scope === undefined || scopePath === undefined
      ? {}
      : {
          scope: Object.freeze({
            roleField: scope.roleField,
            ...(scope.mode === undefined ? {} : { mode: scope.mode }),
            ...(scope.id !== undefined
              ? { idField: scopePath }
              : scope.keys !== undefined ? { keysField: scopePath } : { idsField: scopePath }),
          }),
        }),
  });
};

/** Every included subject must be authorized; a malformed scope fails closed. */
export const scopeContains = (allowed: unknown, required: unknown, mode: 'all' | 'any' = 'all'): boolean =>
  Array.isArray(required) &&
  required.length > 0 &&
  (allowed === undefined ||
    allowed === null ||
    (Array.isArray(allowed) &&
      (mode === 'all'
        ? required.every((id) => allowed.includes(id))
        : required.some((id) => allowed.includes(id)))));

/**
 * Bare workspace-membership predicate. Returns a `Cond` that is true when
 * the actor has a role (any roleId != null) in the workspace identified by
 * the path-variable name `wsIdVar`.
 *
 * This is the membership half of `hasPermission` WITHOUT the
 * `.permissions.hasAny(...)` clause. Use inside a `.when(...)` callback to
 * gate read/list access on workspace membership alone, as required by
 * `business-permissions` rule 3 (MEMBERSHIP_GATES_VIEW_NO_VIEW_PERMISSION).
 *
 * @example
 *   // Org-level entity at `workspaces/{workspaceId}/orders/{orderId}`
 *   can('read').when((_d, _me) => member('workspaceId'))
 *   can('list').when((_d, _me) => member('workspaceId'))
 *
 * The emitted Firestore rule:
 *   inline `get(...users/$(uid)/grants/$(wsId)) != null`
 *
 * The runtime evaluator reads `actor.workspaces[wsId] != null` where
 * `actor.workspaces` is the `{[wsId]: roleId}` map on the actor shape.
 *
 * Safe for `list` rules only while `wsIdVar` names a PARENT path segment.
 * When the listed collection's own wildcard is what carries the workspace id
 * — `workspaces/{workspaceId}` listed at the root — Firestore leaves it
 * unbound during list evaluation and the rule errors into default-deny; the
 * emitter throws at codegen in that case. Reach for `memberOf` instead.
 */
export const member = (wsIdVar: string, options: {readonly envelope?: boolean} = {}): Cond =>
  Object.freeze({
    kind: 'isMember' as const,
    ...(options.envelope === undefined ? {} : {envelope:options.envelope}),
    wsIdVar,
  });

/**
 * Workspace-membership predicate keyed on a field of the document itself:
 * true when the actor holds a role in the workspace named by
 * `resource.data.<wsIdField>`.
 *
 * @example
 *   // workspaces/{workspaceId} — a child workspace denormalizes its org id
 *   can('list').when((data, me) =>
 *     or(data.createdBy.eq(me.uid), memberOf('organizationId')),
 *   )
 *
 *   // Collection-group entity whose doc names its owning organization
 *   can('read').when((_d, _me) => memberOf('workspaceId'))
 *
 * The emitted Firestore rule:
 *   `exists(...grants/$(resource.data.<wsIdField>))
 *      && get(...).data.get('roleId', null) != null`
 *
 * Why this is the list-safe form. `member(wsIdVar)` resolves the workspace id
 * from the match path, and the listed collection's own wildcard is unbound
 * during a list. A document field is always resolvable, and when the client
 * query carries the matching `where('<wsIdField>', '==', id)` Firestore pins
 * the field to a single value for the whole query — so the grant lookup is
 * one document access PER QUERY, never one per result, and no page size can
 * push it into the 10-access ceiling. A list query that does NOT constrain
 * `<wsIdField>` is refused outright, which is the tenant isolation.
 *
 * The runtime evaluator resolves `data[wsIdField]` from the document passed to
 * `me.can(...)` and asserts `actor.workspaces[thatId] != null` — the same
 * grant map the path-keyed form reads, keyed on the doc instead of the actor.
 */
export const memberOf = (wsIdField: string): Cond =>
  Object.freeze({
    kind: 'isMember' as const,
    wsIdVar: wsIdField,
    wsIdField,
  });

/**
 * "Own role" predicate — the self-scoped counterpart to `hasPermission`.
 * Returns a `Cond` that is true when the actor's resolved roleId in the
 * workspace identified by `wsIdVar` equals `idRef` (typically `data.id`).
 * Lets an actor read the ONE role document currently assigned to them
 * without holding the broader permission that gates the rest of the
 * catalog.
 *
 * @example
 *   // Role doc at workspaces/{workspaceId}/roles/{id}
 *   can('read').when((data, me) =>
 *     or(hasPermission(me.permissions, 'workspaces.roles.manage'), ownRole('workspaceId', data.id)),
 *   )
 *
 * The emitted Firestore rule:
 *   inline `exists(...grants/$(wsId)) && get(...).data.get('roleId', null) == <idRef>`
 *
 * The runtime evaluator resolves `actor.workspaces[actor[wsIdVar]]` exactly
 * like `member`, then compares that roleId against `idRef`'s resolved value
 * instead of asserting it is merely non-null.
 *
 * NOT safe for `list` rules — `idField` resolves through `resource.data`
 * (e.g. `resource.data.id`), which Firestore leaves unbound during
 * list-rule evaluation. Gate list access separately with `hasPermission`
 * / `member`.
 */
export const ownRole = (wsIdVar: string, idRef: FieldRef<string>): Cond => {
  const idField = fieldPathOf(idRef);
  if (idField === undefined) {
    throw new Error(
      '@mhmo91/ican: ownRole(...) expected a FieldRef (e.g. data.id) as the second argument',
    );
  }
  return Object.freeze({ kind: 'ownRole' as const, wsIdVar, idField });
};
